import { useCallback } from 'react';
import { captureAttributionData } from '../utils/attribution';

type MatomoCommand = [string, ...(string | number | boolean | null | undefined)[]];

type MatomoQueue = MatomoCommand[];

// Matomo _paq: starts as array, becomes tracker object after script loads
type MatomoPaq = MatomoQueue | { push: (cmd: MatomoCommand) => void };

declare global {
  interface Window {
    _paq?: MatomoPaq;
    dataLayer?: Record<string, unknown>[];
    // fbq is declared in utils/facebookPixel.ts with full type support
  }
}

const isBrowser = typeof window !== 'undefined';

const pushMatomoCommand = (command: MatomoCommand): void => {
  if (!isBrowser) return;
  const paq = window._paq;
  // After Matomo loads, _paq becomes a tracker object (not array) but still has .push()
  if (!paq || typeof paq.push !== 'function') return;
  paq.push(command);
};

export const trackMatomoEvent = (
  category: string,
  action: string,
  name?: string,
  value?: number
): void => {
  if (!isBrowser) return;
  if (value !== undefined) {
    pushMatomoCommand(['trackEvent', category, action, name, value]);
    return;
  }
  if (name !== undefined) {
    pushMatomoCommand(['trackEvent', category, action, name]);
    return;
  }
  pushMatomoCommand(['trackEvent', category, action]);
};

const pushDataLayerEvent = (payload: Record<string, unknown>): void => {
  if (!isBrowser) return;
  if (!window.dataLayer || !Array.isArray(window.dataLayer)) return;
  window.dataLayer.push(payload);
};

const normalizeEventKey = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return 'unknown';
  return trimmed.toLowerCase().replace(/\s+/g, '_');
};

const normalizePath = (path: string): string => {
  if (!isBrowser) return path;
  const trimmed = path.trim();
  if (!trimmed) {
    return `${window.location.pathname}${window.location.search}${window.location.hash}`;
  }
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
};

const isFormConversionEvent = (eventKey: string): boolean => {
  return ['submit', 'submit_success', 'submit_complete', 'conversion', 'success', 'lead'].includes(
    eventKey
  );
};

export function useAnalytics() {
  const trackEvent = useCallback(
    (category: string, action: string, name?: string, value?: number) => {
      if (!isBrowser) return;

      trackMatomoEvent(category, action, name, value);

      const dataLayerPayload: Record<string, unknown> = {
        event: `${category}_${action}`,
        event_category: category,
        event_action: action,
      };

      if (name !== undefined) {
        dataLayerPayload.event_label = name;
      }
      if (value !== undefined) {
        dataLayerPayload.value = value;
      }

      pushDataLayerEvent(dataLayerPayload);

      // GA4 — send same event so GA4 receives client-side behavioral signals
      window.gtag?.('event', `${category}_${action}`, {
        event_category: category,
        ...(name !== undefined && { event_label: name }),
        ...(value !== undefined && { value }),
      });

      if (['lead', 'purchase'].includes(category)) {
        const fbParams: Record<string, unknown> = {};
        if (name !== undefined) {
          fbParams.name = name;
        }
        if (value !== undefined) {
          fbParams.value = value;
        }
        if (window.fbq) {
          window.fbq('trackCustom', `${category}_${action}`, fbParams);
        }
      }
    },
    []
  );

  const trackPageView = useCallback((path: string) => {
    if (!isBrowser) return;

    const normalizedPath = normalizePath(path);
    const pageTitle = typeof document !== 'undefined' ? document.title : undefined;

    pushMatomoCommand(['setCustomUrl', normalizedPath]);
    if (pageTitle) {
      pushMatomoCommand(['setDocumentTitle', pageTitle]);
    }
    pushMatomoCommand(['trackPageView']);

    const pageLocation = normalizedPath.startsWith('http')
      ? normalizedPath
      : `${window.location.origin}${normalizedPath}`;

    const dataLayerPayload: Record<string, unknown> = {
      event: 'page_view',
      page_path: normalizedPath,
      page_location: pageLocation,
    };

    if (pageTitle) {
      dataLayerPayload.page_title = pageTitle;
    }

    pushDataLayerEvent(dataLayerPayload);

    if (window.fbq) {
      window.fbq('track', 'PageView');
    }
  }, []);

  const trackFormEvent = useCallback((formId: string, eventType: string, fieldName?: string) => {
    if (!isBrowser) return;

    const actionKey = normalizeEventKey(eventType);
    const label = fieldName ? `${formId}:${fieldName}` : formId;
    const isConversion = isFormConversionEvent(actionKey);
    const category = isConversion ? 'lead' : 'form';

    trackMatomoEvent(category, actionKey, label);

    // Capture attribution data for consolidated dataLayer event
    const attribution = captureAttributionData();

    const dataLayerPayload: Record<string, unknown> = {
      event: `form_${actionKey}`,
      event_category: category,
      event_action: actionKey,
      event_label: label,
      form_id: formId,
      // Include visitor/session IDs
      sfam_vid: attribution.sfam_vid,
      sfam_sid: attribution.session_id,
      // Include full attribution object for GTM
      attribution: {
        utm_source: attribution.utm_source,
        utm_medium: attribution.utm_medium,
        utm_campaign: attribution.utm_campaign,
        utm_term: attribution.utm_term,
        utm_content: attribution.utm_content,
        gclid: attribution.gclid,
        fbclid: attribution.fbclid,
        referrer: attribution.referrer,
        landing_page: attribution.landing_page,
      },
    };

    if (fieldName) {
      dataLayerPayload.field_name = fieldName;
    }

    pushDataLayerEvent(dataLayerPayload);

    // GA4
    window.gtag?.('event', `form_${actionKey}`, {
      event_category: category,
      event_label: label,
      form_id: formId,
    });

    if (isConversion && window.fbq) {
      const fbParams: Record<string, unknown> = { form_id: formId };
      if (fieldName) {
        fbParams.field_name = fieldName;
      }
      window.fbq('trackCustom', `lead_${actionKey}`, fbParams);
    }
  }, []);

  return { trackEvent, trackPageView, trackFormEvent };
}

export {};
